import { Link, useMatches, createFileRoute, Outlet, isMatch, redirect } from "@tanstack/react-router";
import { AppSidebar } from "@/components/AppSidebar";
import { AsyncJobMenu } from "@/components/AsyncJobMenu";
import { MobileHeader } from "@/components/MobileHeader";
import { PaywallOverlay } from "@/components/PaywallOverlay";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { getIsSidebarDefaultOpen, SidebarProvider, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { usePendo } from "@/hooks/use-pendo";
import { cn } from "@/lib/utils/common";

export const Route = createFileRoute("/(authed)/_layout")({
  component: RouteComponent,
  beforeLoad: ({ context, location }) => {
    if (!context.auth?.isAuthenticated) {
      redirect({
        to: "/login",
        search: {
          redirect: location.href,
        },
        throw: true,
      });

      return;
    }
  },
});

function RouteComponent() {
  usePendo();

  return (
    <div className="flex flex-col">
      <SidebarProvider defaultOpen={getIsSidebarDefaultOpen()}>
        <MobileHeader />
        <AppSidebar />
        <Main />
        <AsyncJobMenu />
        <PaywallOverlay />
      </SidebarProvider>
    </div>
  );
}

function Main() {
  const { isMobile, sidebarWidth } = useSidebar();

  return (
    <main className={cn("flex flex-1 flex-col", isMobile ? "pt-10" : "")}>
      <div className="flex p-4 items-center">
        <SidebarTrigger />
        {isMobile ? null : <Breadcrumbs />}
      </div>
      <div className="flex flex-1 flex-col print:!w-full" style={{ width: `calc(100vw - ${sidebarWidth})` }}>
        <Outlet />
      </div>
    </main>
  );
}

function Breadcrumbs() {
  const matches = useMatches();

  const matchesWithCrumbs = matches.filter((match) => isMatch(match, "loaderData.crumb"));

  const items = matchesWithCrumbs.map(({ pathname, loaderData }) => {
    return {
      href: pathname,
      label: loaderData?.crumb,
    };
  });

  if (items.length <= 1) {
    return null;
  }

  return (
    <>
      {items.length > 1 && <div className="mx-2 w-px h-5 bg-gray-300" />}
      <Breadcrumb>
        <BreadcrumbList>
          {items.map((item, index) => (
            <BreadcrumbItem key={item.href} className={index === items.length - 1 ? "text-black" : ""}>
              <BreadcrumbLink asChild>
                <Link to={item.href}>{item.label}</Link>
              </BreadcrumbLink>
              {index < items.length - 1 && <BreadcrumbSeparator />}
            </BreadcrumbItem>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    </>
  );
}
